import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Activity,
  AlertTriangle,
  BriefcaseBusiness,
  CalendarClock,
  Euro,
  FileClock,
  PhoneCall,
  Send,
  ShieldAlert,
  TrendingUp,
  UserRoundSearch,
  UserRoundX,
  Users,
} from "lucide-react";

import api from "../../../lib/api";
import { useAuth } from "../../../context/AuthContext";
import { Badge } from "../../../components/ui/Badge";

interface Metrics {
  prospects: number;
  clientes: number;
  oportunidades_abiertas: number;
  propuestas_pendientes: number;
  cotizaciones_pendientes: number;
  renovaciones_proximas: number;
  prima_total_mes_cents: number;
  comision_mes_cents: number;
  pipeline_cents: number;
  pipeline_ponderado_cents: number;
  tareas_vencidas: number;
  tareas_hoy: number;
  llamadas_hoy: number;
  reuniones_hoy: number;
  actividad_7_dias: number;
  prospects_sin_actividad: number;
}

interface RecentActivity {
  id: number;
  id_externo: string | null;
  tipo: string;
  asunto: string;
  realizada_at: string;
  empresa_id_externo: string | null;
  empresa: string;
  contacto: string | null;
  autor_nombre: string | null;
  autor_email: string | null;
}

interface NextTask {
  id: number;
  id_externo: string | null;
  titulo: string;
  vence_at: string;
  prioridad: "baja" | "normal" | "alta";
  empresa_id_externo: string | null;
  empresa: string;
  contacto: string | null;
}

interface InactiveProspect {
  id: number;
  id_externo: string | null;
  empresa: string;
  created_at: string;
  ultima_actividad_at: string | null;
}

interface ResumenResponse {
  metrics: Metrics;
  recentActivities: RecentActivity[];
  nextTasks: NextTask[];
  inactiveProspects: InactiveProspect[];
  inactiveDays: number;
  canViewAllAgendas: boolean;
  selectedResponsible: "all" | number;
}

interface AgendaResponsible {
  id: number;
  nombre: string | null;
  email: string;
}

type SummaryTargetTab = "clientes" | "oportunidades" | "agenda" | "polizas";

interface CrmResumenTabProps {
  onOpenTab: (
    tab: SummaryTargetTab,
    params?: Record<string, string>
  ) => void;
}

const EMPTY_METRICS: Metrics = {
  prospects: 0,
  clientes: 0,
  oportunidades_abiertas: 0,
  propuestas_pendientes: 0,
  cotizaciones_pendientes: 0,
  renovaciones_proximas: 0,
  prima_total_mes_cents: 0,
  comision_mes_cents: 0,
  pipeline_cents: 0,
  pipeline_ponderado_cents: 0,
  tareas_vencidas: 0,
  tareas_hoy: 0,
  llamadas_hoy: 0,
  reuniones_hoy: 0,
  actividad_7_dias: 0,
  prospects_sin_actividad: 0,
};

function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError<{ error?: string }>(error)) {
    return error.response?.data?.error ?? "No se pudo cargar el resumen";
  }
  return "No se pudo cargar el resumen";
}

function formatMoney(cents: number): string {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(Number(cents || 0) / 100);
}

function formatExactMoney(cents: number): string {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(cents || 0) / 100);
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat("es-ES", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
}

export function CrmResumenTab({ onOpenTab }: CrmResumenTabProps) {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState<Metrics>(EMPTY_METRICS);
  const [activities, setActivities] = useState<RecentActivity[]>([]);
  const [tasks, setTasks] = useState<NextTask[]>([]);
  const [inactiveProspects, setInactiveProspects] = useState<InactiveProspect[]>([]);
  const [inactiveDays, setInactiveDays] = useState(30);
  const [selectedResponsible, setSelectedResponsible] = useState("me");
  const [responsibles, setResponsibles] = useState<AgendaResponsible[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get<ResumenResponse>(
        "/admin/crm/seguimiento/resumen",
        {
          params: {
            inactive_days: inactiveDays,
            responsable_user_id: selectedResponsible,
          },
        }
      );
      setMetrics(response.data.metrics);
      setActivities(response.data.recentActivities);
      setTasks(response.data.nextTasks);
      setInactiveProspects(response.data.inactiveProspects);
    } catch (loadError) {
      setError(getErrorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, [inactiveDays, selectedResponsible]);

  useEffect(() => {
    if (!user?.crmSupervisor) return;
    api
      .get<{ data: AgendaResponsible[] }>("/admin/crm/seguimiento/responsables-agenda")
      .then((response) => setResponsibles(response.data.data))
      .catch(() => setResponsibles([]));
  }, [user?.crmSupervisor]);

  useEffect(() => {
    void load();

    const refresh = () => void load();
    window.addEventListener("crm:seguimiento-actualizado", refresh);

    return () => {
      window.removeEventListener("crm:seguimiento-actualizado", refresh);
    };
  }, [load]);

  const agendaParams = (params: Record<string, string>) => ({
    ...params,
    estado: "pendiente",
    responsable_user_id: selectedResponsible,
  });

  const cards = [
    {
      label: "Prospects",
      value: metrics.prospects,
      icon: UserRoundSearch,
      color: "text-blue-700 bg-blue-50 border-blue-200",
      onClick: () =>
        onOpenTab("clientes", {
          estado: "activo",
          estado_comercial: "prospect",
        }),
    },
    {
      label: "Clientes",
      value: metrics.clientes,
      icon: Users,
      color: "text-green-700 bg-green-50 border-green-200",
      onClick: () =>
        onOpenTab("clientes", {
          estado: "activo",
          estado_comercial: "cliente",
        }),
    },
    {
      label: "Oportunidades abiertas",
      value: metrics.oportunidades_abiertas,
      icon: BriefcaseBusiness,
      color: "text-indigo-700 bg-indigo-50 border-indigo-200",
      onClick: () => onOpenTab("oportunidades", { estado: "abierta" }),
    },
    {
      label: "Propuestas pendientes",
      value: metrics.propuestas_pendientes,
      icon: Send,
      color: "text-fuchsia-700 bg-fuchsia-50 border-fuchsia-200",
      onClick: () =>
        onOpenTab("oportunidades", {
          estado: "abierta",
          etapa_codigos: "PROPUESTA_ENVIADA,NEGOCIACION",
          vista: "propuestas_pendientes",
        }),
    },
    {
      label: "Cotizaciones pendientes",
      value: metrics.cotizaciones_pendientes,
      icon: FileClock,
      color: "text-sky-700 bg-sky-50 border-sky-200",
      onClick: () =>
        onOpenTab("oportunidades", {
          estado: "abierta",
          etapa_codigos: "SOLICITANDO_COTIZACION",
          vista: "cotizaciones_pendientes",
        }),
    },
    {
      label: "Renovaciones próximas (60 días)",
      value: metrics.renovaciones_proximas,
      icon: ShieldAlert,
      color: "text-rose-700 bg-rose-50 border-rose-200",
      onClick: () => onOpenTab("polizas", { renovacion_dias: "60" }),
    },
    {
      label: "Prima total del mes",
      value: formatExactMoney(metrics.prima_total_mes_cents),
      icon: Euro,
      color: "text-emerald-700 bg-emerald-50 border-emerald-200",
      onClick: () => onOpenTab("polizas"),
    },
    {
      label: "Comisión Circuit Cover del mes",
      value: formatExactMoney(metrics.comision_mes_cents),
      icon: TrendingUp,
      color: "text-lime-700 bg-lime-50 border-lime-200",
      onClick: () => onOpenTab("polizas"),
    },
    {
      label: "Valor del pipeline",
      value: formatMoney(metrics.pipeline_cents),
      icon: Euro,
      color: "text-violet-700 bg-violet-50 border-violet-200",
      onClick: () => onOpenTab("oportunidades", { estado: "abierta" }),
    },
    {
      label: "Pipeline ponderado",
      value: formatMoney(metrics.pipeline_ponderado_cents),
      icon: TrendingUp,
      color: "text-cyan-700 bg-cyan-50 border-cyan-200",
      onClick: () => onOpenTab("oportunidades", { estado: "abierta" }),
    },
    {
      label: "Tareas vencidas",
      value: metrics.tareas_vencidas,
      icon: AlertTriangle,
      color: "text-red-700 bg-red-50 border-red-200",
      onClick: () => onOpenTab("agenda", agendaParams({ periodo: "vencidas" })),
    },
    {
      label: "Tareas para hoy",
      value: metrics.tareas_hoy,
      icon: CalendarClock,
      color: "text-amber-700 bg-amber-50 border-amber-200",
      onClick: () => onOpenTab("agenda", agendaParams({ periodo: "hoy" })),
    },
    {
      label: "Llamadas para hoy",
      value: metrics.llamadas_hoy,
      icon: PhoneCall,
      color: "text-blue-700 bg-blue-50 border-blue-200",
      onClick: () =>
        onOpenTab("agenda", agendaParams({ periodo: "hoy", tipo: "llamada" })),
    },
    {
      label: "Reuniones para hoy",
      value: metrics.reuniones_hoy,
      icon: CalendarClock,
      color: "text-teal-700 bg-teal-50 border-teal-200",
      onClick: () =>
        onOpenTab("agenda", agendaParams({ periodo: "hoy", tipo: "reunion" })),
    },
    {
      label: "Actividad últimos 7 días",
      value: metrics.actividad_7_dias,
      icon: Activity,
      color: "text-gray-700 bg-gray-50 border-gray-200",
      onClick: () =>
        document
          .getElementById("crm-actividad-reciente")
          ?.scrollIntoView({ behavior: "smooth", block: "start" }),
    },
    {
      label: `Prospects sin actividad (${inactiveDays} días)`,
      value: metrics.prospects_sin_actividad,
      icon: UserRoundX,
      color: "text-orange-700 bg-orange-50 border-orange-200",
      onClick: () =>
        onOpenTab("clientes", {
          estado: "activo",
          estado_comercial: "prospect",
          sin_actividad_dias: String(inactiveDays),
        }),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Resumen comercial</h2>
          <p className="mt-1 text-sm text-gray-600">
            Situación actual de la cartera, el pipeline y el seguimiento pendiente.
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          {user?.crmSupervisor && (
            <label className="block text-sm text-gray-700">
              <span className="mb-1 block font-medium">Seguimiento de</span>
              <select
                value={selectedResponsible}
                onChange={(event) => setSelectedResponsible(event.target.value)}
                className="min-h-11 rounded-lg border border-gray-300 bg-white px-3 py-2"
              >
                <option value="me">Mi actividad</option>
                <option value="all">Todo el equipo</option>
                {responsibles
                  .filter((responsible) => responsible.id !== user.id)
                  .map((responsible) => (
                    <option key={responsible.id} value={responsible.id}>
                      {responsible.nombre || responsible.email}
                    </option>
                  ))}
              </select>
            </label>
          )}
          <label className="block text-sm text-gray-700">
            <span className="mb-1 block font-medium">Alerta sin actividad</span>
            <select
              value={inactiveDays}
              onChange={(event) => setInactiveDays(Number(event.target.value))}
              className="min-h-11 rounded-lg border border-gray-300 bg-white px-3 py-2"
            >
              <option value={15}>15 días</option>
              <option value={30}>30 días</option>
              <option value={60}>60 días</option>
              <option value={90}>90 días</option>
            </select>
          </label>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map(({ label, value, icon: Icon, color, onClick }) => (
          <button
            key={label}
            type="button"
            onClick={onClick}
            aria-label={`Ver detalle de ${label}`}
            className={`group cursor-pointer rounded-lg border p-4 text-left transition hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A3AB8] focus-visible:ring-offset-2 ${color}`}
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-2xl font-bold">{loading ? "…" : value}</div>
                <div className="mt-1 text-sm">{label}</div>
              </div>
              <Icon className="h-6 w-6 opacity-70 transition-transform group-hover:scale-110" />
            </div>
          </button>
        ))}
      </div>

      <div className="grid gap-5 xl:grid-cols-2">
        <section className="rounded-lg border border-gray-200 p-4">
          <h3 className="font-semibold text-gray-900">Próximas acciones</h3>
          <div className="mt-3 space-y-2">
            {!loading && tasks.length === 0 ? (
              <p className="rounded-lg bg-gray-50 py-8 text-center text-sm text-gray-500">
                No hay tareas pendientes.
              </p>
            ) : (
              tasks.map((task) => (
                <div key={task.id} className="rounded-lg border border-gray-200 p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-gray-900">{task.titulo}</span>
                    {task.prioridad === "alta" && <Badge variant="danger">Alta</Badge>}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {formatDateTime(task.vence_at)} · {task.empresa}
                    {task.contacto ? ` · ${task.contacto}` : ""}
                  </p>
                </div>
              ))
            )}
          </div>
        </section>

        <section
          id="crm-actividad-reciente"
          className="scroll-mt-6 rounded-lg border border-gray-200 p-4"
        >
          <h3 className="font-semibold text-gray-900">Actividad reciente</h3>
          <div className="mt-3 space-y-2">
            {!loading && activities.length === 0 ? (
              <p className="rounded-lg bg-gray-50 py-8 text-center text-sm text-gray-500">
                Todavía no hay actividad comercial.
              </p>
            ) : (
              activities.map((activity) => (
                <div key={activity.id} className="rounded-lg border border-gray-200 p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="neutral">{activity.tipo}</Badge>
                    <span className="font-medium text-gray-900">{activity.asunto}</span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {formatDateTime(activity.realizada_at)} · {activity.empresa}
                    {activity.contacto ? ` · ${activity.contacto}` : ""}
                    {activity.autor_nombre || activity.autor_email
                      ? ` · ${activity.autor_nombre || activity.autor_email}`
                      : ""}
                  </p>
                </div>
              ))
            )}
          </div>
        </section>
      </div>

      <section className="rounded-lg border border-orange-200 bg-orange-50/40 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="font-semibold text-gray-900">Prospects sin actividad</h3>
            <p className="mt-1 text-sm text-gray-600">
              Empresas que llevan más de {inactiveDays} días sin seguimiento comercial.
            </p>
          </div>
          <Badge variant={inactiveProspects.length > 0 ? "warning" : "success"}>
            {loading ? "…" : metrics.prospects_sin_actividad}
          </Badge>
        </div>

        <div className="mt-3 space-y-2">
          {!loading && inactiveProspects.length === 0 ? (
            <p className="rounded-lg bg-white py-6 text-center text-sm text-gray-500">
              No hay prospects desatendidos con este plazo.
            </p>
          ) : (
            inactiveProspects.map((prospect) => (
              <div
                key={prospect.id}
                className="rounded-lg border border-orange-200 bg-white px-4 py-3"
              >
                <div className="font-medium text-gray-900">{prospect.empresa}</div>
                <div className="mt-1 text-xs text-gray-500">
                  {prospect.id_externo ?? "Prospect sin ID de cliente"} · {prospect.ultima_actividad_at
                    ? `Última actividad: ${formatDateTime(prospect.ultima_actividad_at)}`
                    : `Creado: ${formatDateTime(prospect.created_at)} · Sin actividad registrada`}
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
